import React, { useCallback, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useLogin } from "../../hooks/useLogin";
import { useAuthRedirect } from "../../hooks/useAuthRedirect";
import { useOTPFlow } from "../../hooks/useOTPFlow";
import { useResendCooldown } from "../../hooks/useResendCooldown";
import { EmailForm } from "./components/EmailForm";
import { OTPForm } from "./components/OTPForm";
import { AUTH_CONSTANTS } from "../../constants/auth";
import { Box, Typography, Link as MuiLink } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

export const LoginView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { requestOTP, verifyOTP, error, isLoading, otpSent } = useLogin();
  const {
    email,
    otpCode,
    showOtpInput,
    setEmail,
    setOtpCode,
    showOtpScreen,
    backToEmailScreen,
    isEmailValid,
    isOtpValid,
  } = useOTPFlow();
  const { cooldown, startCooldown, resetCooldown } = useResendCooldown(
    AUTH_CONSTANTS.RESEND_COOLDOWN_SECONDS
  );

  useAuthRedirect();

  useEffect(() => {
    if (otpSent) {
      showOtpScreen();
    }
  }, [otpSent, showOtpScreen]);

  const handleRequestOTP = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!email || !isEmailValid) {
        return;
      }

      const success = await requestOTP(email);

      if (success) {
        startCooldown();
      }
    },
    [email, isEmailValid, requestOTP, startCooldown]
  );

  const handleVerifyOTP = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!email || !isOtpValid) {
        return;
      }

      const result = await verifyOTP(email, otpCode);

      if (result?.isAuth) {
        const from =
          location.state?.from?.pathname ||
          AUTH_CONSTANTS.DEFAULT_REDIRECT_PATH;
        navigate(from, { replace: true });
      }
    },
    [email, otpCode, isOtpValid, verifyOTP, navigate, location]
  );

  const handleResendOTP = useCallback(async () => {
    if (cooldown > 0) {
      return;
    }

    setOtpCode("");
    resetCooldown();
    const success = await requestOTP(email);

    if (success) {
      startCooldown();
    }
  }, [cooldown, email, requestOTP, setOtpCode, startCooldown, resetCooldown]);

  const handleBackToEmail = useCallback(() => {
    backToEmailScreen();
  }, [backToEmailScreen]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#2a3040",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background gradients */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at top, rgba(138,17,37,0.1), transparent 60%), radial-gradient(ellipse at bottom right, rgba(16,50,74,0.15), transparent 60%)",
          pointerEvents: "none",
        }}
      />

      {/* Subtle grid pattern */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.02,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          pointerEvents: "none",
        }}
      />

      {/* Content */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 420,
          mx: "auto",
          px: 2.5,
          py: 4,
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            mb: 5,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: "#8a1125",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(to bottom, transparent, rgba(255,255,255,0.1))",
              }}
            />
            <Typography
              sx={{
                color: "white",
                fontFamily: '"Outfit", sans-serif',
                fontWeight: 700,
                fontSize: "20px",
                position: "relative",
                zIndex: 1,
              }}
            >
              V
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                color: "white",
                fontFamily: '"Outfit", sans-serif',
                fontWeight: 600,
                fontSize: "20px",
                lineHeight: 1.2,
              }}
            >
              Varna
            </Typography>
            <Typography
              sx={{
                color: "white",
                fontFamily: '"Outfit", sans-serif',
                fontWeight: 500,
                fontSize: "11px",
                textTransform: "uppercase",
                letterSpacing: "0.3em",
              }}
            >
              Adminpanel
            </Typography>
          </Box>
        </Box>

        {/* Frosted glass card */}
        <Box
          sx={{
            background: "rgba(255,255,255,0.03)",
            backdropFilter: "blur(8px)",
            borderRadius: "24px",
            border: "1px solid rgba(255,255,255,0.06)",
            p: { xs: 4, sm: 5 },
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "16px",
                background: "rgba(138,17,37,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2.5,
              }}
            >
              <LockOutlinedIcon
                sx={{ color: "#c41830", fontSize: 28 }}
              />
            </Box>
            <Typography
              sx={{
                color: "white !important",
                fontFamily: '"Outfit", sans-serif',
                fontWeight: 600,
                fontSize: "24px",
                mb: 0.5,
              }}
            >
              Logga in
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.6) !important",
                fontSize: "14px",
              }}
            >
              Ange din e-post för att fortsätta
            </Typography>
          </Box>

          {!showOtpInput ? (
            <EmailForm
              email={email}
              onEmailChange={setEmail}
              onSubmit={handleRequestOTP}
              error={error}
              isLoading={isLoading}
              isEmailValid={isEmailValid}
            />
          ) : (
            <OTPForm
              email={email}
              otpCode={otpCode}
              onOtpChange={setOtpCode}
              onSubmit={handleVerifyOTP}
              onBackToEmail={handleBackToEmail}
              onResendOTP={handleResendOTP}
              error={error}
              isLoading={isLoading}
              isOtpValid={isOtpValid}
              resendCooldown={cooldown}
            />
          )}
        </Box>

        {/* Footer links */}
        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: "1px solid rgba(255,255,255,0.04)",
            display: "flex",
            justifyContent: "center",
            gap: 3,
          }}
        >
          <MuiLink
            component={Link}
            to="/privacy"
            variant="body2"
            sx={{
              color: "rgba(255,255,255,0.4)",
              textDecoration: "none",
              "&:hover": { color: "rgba(255,255,255,0.7)" },
              transition: "color 0.3s",
            }}
          >
            Integritetspolicy
          </MuiLink>
          <MuiLink
            component={Link}
            to="/support"
            variant="body2"
            sx={{
              color: "rgba(255,255,255,0.4)",
              textDecoration: "none",
              "&:hover": { color: "rgba(255,255,255,0.7)" },
              transition: "color 0.3s",
            }}
          >
            Support
          </MuiLink>
        </Box>
      </Box>
    </Box>
  );
};
